import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function MyAccountDialog({ open, onOpenChange, onSaved }) {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busyProfile, setBusyProfile] = useState(false);
  const [busyPw, setBusyPw] = useState(false);

  useEffect(() => {
    if (open && user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
    }
  }, [open, user]);

  const saveProfile = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusyProfile(true);
    try {
      await api.patch("/auth/me", { name: name.trim(), email: email.trim().toLowerCase() });
      await refreshUser();
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusyProfile(false);
    }
  };

  const changePw = async () => {
    if (!curPw) {
      toast.error("Enter your current password");
      return;
    }
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New passwords do not match");
      return;
    }
    setBusyPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password changed successfully");
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusyPw(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="my-account-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--navy)]">My Account</DialogTitle>
          <DialogDescription>Update your login email, display name or password.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <UserCog className="w-4 h-4 text-[var(--gold)]" />
              <span className="hotel-overline">Profile</span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Full name</Label>
              <Input data-testid="account-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-white border-[var(--border-warm)]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Email (login ID)</Label>
              <Input data-testid="account-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white border-[var(--border-warm)] font-mono-hotel" />
            </div>
            <Button data-testid="account-save-profile-button" onClick={saveProfile} disabled={busyProfile} className="w-full bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
              {busyProfile ? "Saving…" : "Save Profile"}
            </Button>
          </div>
          <div className="border-t border-[var(--border-subtle)] pt-5 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[var(--gold)]" />
              <span className="hotel-overline">Change Password</span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Current password</Label>
              <Input data-testid="account-current-password-input" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="mt-1.5 bg-white border-[var(--border-warm)]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">New password</Label>
                <Input data-testid="account-new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5 bg-white border-[var(--border-warm)]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Confirm new</Label>
                <Input data-testid="account-confirm-password-input" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5 bg-white border-[var(--border-warm)]" />
              </div>
            </div>
            <Button data-testid="account-change-password-button" onClick={changePw} disabled={busyPw} variant="outline" className="w-full border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold-light)]">
              {busyPw ? "Changing…" : "Change Password"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
